import * as React from "react"
import { cva } from "class-variance-authority"
import { AlertTriangle, CheckCircle2, Info, XCircle } from "lucide-react"

import { cn } from "../../lib/utils"

const alertVariants = cva(
  "relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive:
          "border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive",
        success:
          "border-success/50 bg-success/10 text-success dark:border-success [&>svg]:text-success",
        warning:
          "border-warning/50 bg-warning/10 text-warning dark:border-warning [&>svg]:text-warning",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const Alert = React.forwardRef(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
))
Alert.displayName = "Alert"

const AlertTitle = React.forwardRef(({ className, children, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("mb-1 font-medium leading-none tracking-tight", className)}
    {...props}
  >
    {children}
  </h5>
))
AlertTitle.displayName = "AlertTitle"

const AlertDescription = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm [&_p]:leading-relaxed", className)}
    {...props}
  />
))
AlertDescription.displayName = "AlertDescription"

// Convenience components with built-in icons
const AlertSuccess = React.forwardRef(({ children, className, ...props }, ref) => (
  <Alert ref={ref} variant="success" className={className} {...props}>
    <CheckCircle2 className="h-4 w-4" />
    {children}
  </Alert>
))
AlertSuccess.displayName = "AlertSuccess"

const AlertWarning = React.forwardRef(({ children, className, ...props }, ref) => (
  <Alert ref={ref} variant="warning" className={className} {...props}>
    <AlertTriangle className="h-4 w-4" />
    {children}
  </Alert>
))
AlertWarning.displayName = "AlertWarning"

const AlertError = React.forwardRef(({ children, className, ...props }, ref) => (
  <Alert ref={ref} variant="destructive" className={className} {...props}>
    <XCircle className="h-4 w-4" />
    {children}
  </Alert>
))
AlertError.displayName = "AlertError"

const AlertInfo = React.forwardRef(({ children, className, ...props }, ref) => (
  <Alert ref={ref} variant="default" className={className} {...props}>
    <Info className="h-4 w-4" />
    {children}
  </Alert>
))
AlertInfo.displayName = "AlertInfo"

export { 
  Alert, 
  AlertTitle, 
  AlertDescription, 
  AlertSuccess, 
  AlertWarning, 
  AlertError, 
  AlertInfo,
  alertVariants 
}
